import { useEffect, useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown } from "lucide-react";
import api from "@/lib/api";

/**
 * Autocomplete for assignee. Value is the name string. onChange receives (name, email).
 * Suggestions loaded from /api/assignees. User can freely type new values.
 */
export default function AssigneeCombobox({
  name,
  email,
  onChange,
  namePlaceholder = "e.g. Alice Chen",
  emailPlaceholder = "alice@example.com",
  nameTestId = "assignee-name-input",
  emailTestId = "assignee-email-input",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    api
      .get("/assignees")
      .then((res) => setSuggestions(res.data))
      .catch(() => setSuggestions([]));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = (name || "").trim().toLowerCase();
  const filtered = q
    ? suggestions.filter(
        (s) => s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q)
      )
    : suggestions;

  const pick = (s) => {
    onChange(s.name, s.email);
    setOpen(false);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1.5 relative" ref={wrapRef}>
        <label className="text-sm font-medium text-slate-700 flex items-center justify-between">
          Assignee name
          {suggestions.length > 0 && (
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-0.5"
              onClick={() => setOpen((v) => !v)}
              data-testid="assignee-suggestions-toggle"
            >
              {suggestions.length} saved <ChevronDown size={12} />
            </button>
          )}
        </label>
        <Input
          required
          value={name}
          onChange={(e) => {
            onChange(e.target.value, email);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={namePlaceholder}
          data-testid={nameTestId}
          autoComplete="off"
        />
        {open && filtered.length > 0 && (
          <div
            className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-md max-h-56 overflow-auto"
            data-testid="assignee-suggestions"
          >
            {filtered.map((s) => {
              const selected = s.email.toLowerCase() === (email || "").toLowerCase();
              return (
                <button
                  type="button"
                  key={s.email}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 text-sm"
                  onClick={() => pick(s)}
                  data-testid={`assignee-suggestion-${s.email}`}
                >
                  <div className="min-w-0">
                    <div className="text-slate-900 font-medium truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.email}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-xs text-slate-400">{s.count} task{s.count === 1 ? "" : "s"}</span>
                    {selected && <Check size={14} className="text-emerald-600" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-700">Assignee email</label>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={emailPlaceholder}
          data-testid={emailTestId}
          autoComplete="off"
        />
      </div>
    </div>
  );
}
